import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

/** VH-224: sleep was nearly constant, so it cannot explain changes; offers an experiment. */
export default function ConstancyNotice({ constancy, onStartExperiment, starting = false, canStart = true }) {
  const { t } = useTranslation()
  const suggestion = constancy?.experiment_suggestion
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState(suggestion?.suggested_target_minutes ?? 420)

  if (!constancy?.applies) return null

  return (
    <section className="border border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="constancy-notice">
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold">{t('sleepRecovery.constancy.title')}</h3>
          <p className="text-sm mt-1">
            {t('sleepRecovery.constancy.body', {
              low: constancy.lowest_hours?.toFixed(2),
              high: constancy.highest_hours?.toFixed(2),
              range: constancy.range_minutes,
              nights: constancy.nights,
              months: constancy.months,
            })}
          </p>
          {suggestion && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-sm font-medium text-primary underline mt-2">
              {open ? t('sleepRecovery.constancy.hide') : t('sleepRecovery.constancy.open')}
            </button>
          )}
          {open && suggestion && (
            <div className="mt-3 space-y-2 text-sm" data-testid="experiment-suggestion">
              <p>{t('sleepRecovery.experiment.suggestion', {
                days: suggestion.duration_days,
                baseline: suggestion.baseline_minutes,
                target: suggestion.suggested_target_minutes,
              })}</p>
              <p>{t('sleepRecovery.experiment.evidence', {
                none: suggestion.no_effect_extra_minutes,
                from: suggestion.effective_extra_minutes[0],
                to: suggestion.effective_extra_minutes[suggestion.effective_extra_minutes.length - 1],
              })}</p>
              <p>{t('sleepRecovery.experiment.compares', {
                metrics: suggestion.compared_metrics.map((m) => t(`sleepRecovery.metrics.${m}`)).join(', '),
              })}</p>
              {canStart && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col text-xs">
                    {t('sleepRecovery.experiment.targetLabel')}
                    <input
                      type="number" min={300} max={720} step={5} value={target}
                      onChange={(e) => setTarget(Number(e.target.value))}
                      className="border rounded px-2 py-1 w-28 text-sm"
                    />
                  </label>
                  <button
                    type="button" disabled={starting || target < 300 || target > 720}
                    onClick={() => onStartExperiment?.(target)}
                    className="bg-primary text-white rounded px-3 py-1.5 text-sm disabled:opacity-50"
                  >
                    {t('sleepRecovery.experiment.start')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
